import {
  useState, useMemo 
} from 'react';
import {
  MessageSquare,
  Search,
  X,
  Award,
  Users,
  ThumbsUp,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  useFeedback,
  FeedbackCard,
  FeedbackDetailModal,
} from '@/features/performance';
import type {
  FeedbackType, Feedback 
} from '@/features/performance';
import { toast } from 'sonner';
import { FEEDBACK_TYPES } from './performance-constants';
import {
  FeedbackFormModal, type FeedbackFormData 
} from './feedback-form-modal';

export function FeedbackView() {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<FeedbackType | ''>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [formData, setFormData] = useState<FeedbackFormData>({
    employeeName: '',
    type: '',
    category: '',
    title: '',
    content: '',
    rating: '',
  });

  const {
    data, isLoading, error, refetch 
  } = useFeedback({
    filters: {
      search: searchQuery || undefined,
      type: typeFilter || undefined,
    },
  });

  const feedbackList = data?.data ?? [];

  const stats = useMemo(() => {
    const praise = feedbackList.filter((f) => f.type === 'praise').length;
    const recognition = feedbackList.filter((f) => f.type === 'recognition').length;
    const acknowledged = feedbackList.filter((f) => f.status === 'acknowledged').length;
    return {
      praise,
      recognition,
      acknowledged,
      total: feedbackList.length,
    };
  }, [feedbackList]);

  const handleAddFeedback = () => {
    setFormData({
      employeeName: '',
      type: '',
      category: '',
      title: '',
      content: '',
      rating: '',
    });
    setEditingFeedback(null);
    setShowAddModal(true);
  };

  const handleEditFeedback = (feedback: Feedback) => {
    setFormData({
      employeeName: feedback.employeeName,
      type: feedback.type,
      category: feedback.category,
      title: feedback.title,
      content: feedback.content,
      rating: feedback.rating?.toString() ?? '',
    });
    setEditingFeedback(feedback.feedbackId);
    setSelectedFeedback(null);
    setShowAddModal(true);
  };

  const handleDeleteFeedback = (feedback: Feedback) => {
    toast.success(`Deleted feedback: ${feedback.title}`);
    setSelectedFeedback(null);
    refetch();
  };

  const handleAcknowledgeFeedback = (feedback: Feedback) => {
    toast.success(`Acknowledged feedback for ${feedback.employeeName}`);
    setSelectedFeedback(null);
    refetch();
  };

  const handleSaveFeedback = () => {
    if (!formData.employeeName || !formData.type || !formData.title || !formData.content) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (editingFeedback) {
      toast.success('Feedback updated');
    } else {
      toast.success('Feedback submitted');
    }
    setShowAddModal(false);
    refetch();
  };

  const handleCardClick = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
  };

  const exportData = feedbackList.map((f) => ({
    employee: f.employeeName,
    role: f.employeeRole,
    type: f.type,
    category: f.category,
    title: f.title,
    content: f.content,
    rating: f.rating ?? '',
    status: f.status,
    submittedBy: f.submittedByName,
    date: f.createdAt,
  }));

  return (
    <>
      <CrudToolbar
        entityName="Feedback"
        entityNamePlural="Feedback"
        selectedCount={0}
        onAdd={handleAddFeedback}
        exportData={exportData}
        exportFilename="performance-feedback.csv"
      />

      <FeedbackFormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveFeedback}
        formData={formData}
        setFormData={setFormData}
        isEditing={!!editingFeedback}
      />

      <FeedbackDetailModal
        feedback={selectedFeedback}
        isOpen={!!selectedFeedback}
        onClose={() => setSelectedFeedback(null)}
        onEdit={handleEditFeedback}
        onDelete={handleDeleteFeedback}
        onAcknowledge={handleAcknowledgeFeedback}
      />

      <StatsCards stats={stats} />

      <SearchAndFilter
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
      />

      <FeedbackGrid
        feedbackList={feedbackList}
        isLoading={isLoading}
        error={error}
        onCardClick={handleCardClick}
        onRefetch={refetch}
      />
    </>
  );
}

interface StatsCardsProps {
  readonly stats: {
    total: number;
    praise: number;
    recognition: number;
    acknowledged: number;
  };
}

function StatsCards({ stats }: StatsCardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Feedback</CardTitle>
          <MessageSquare className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Praise</CardTitle>
          <ThumbsUp className="h-5 w-5 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{stats.praise}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Recognition</CardTitle>
          <Award className="h-5 w-5 text-purple-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-purple-600">{stats.recognition}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Acknowledged</CardTitle>
          <Users className="h-5 w-5 text-blue-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">{stats.acknowledged}</div>
        </CardContent>
      </Card>
    </div>
  );
}

interface SearchAndFilterProps {
  readonly searchQuery: string;
  readonly setSearchQuery: (query: string) => void;
  readonly typeFilter: FeedbackType | '';
  readonly setTypeFilter: (type: FeedbackType | '') => void;
}

function SearchAndFilter({
  searchQuery,
  setSearchQuery,
  typeFilter,
  setTypeFilter,
}: SearchAndFilterProps) {
  return (
    <div className="flex gap-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search feedback..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <select
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value as FeedbackType | '')}
        className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="">All Types</option>
        {FEEDBACK_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface FeedbackGridProps {
  readonly feedbackList: Feedback[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly onCardClick: (feedback: Feedback) => void;
  readonly onRefetch: () => void;
}

function FeedbackGrid({
  feedbackList,
  isLoading,
  error,
  onCardClick,
  onRefetch,
}: FeedbackGridProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`skel-${i.toString()}`} className="h-64 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <MessageSquare className="mb-4 h-12 w-12 text-red-500" />
          <p className="text-lg font-medium">Failed to load feedback</p>
          <Button variant="outline" className="mt-4" onClick={onRefetch}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (feedbackList.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <MessageSquare className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium">No feedback found</p>
          <p className="text-muted-foreground">Submit feedback to get started</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {feedbackList.map((feedback) => (
        <FeedbackCard
          key={feedback.feedbackId}
          feedback={feedback}
          onClick={() => onCardClick(feedback)}
        />
      ))}
    </div>
  );
}
